
// import { Send, MessageSquare } from 'lucide-react';
// import { enviarPruebaWhatsApp } from '../../services/twilioTest';

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from '../../firebase/config';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';

const DashboardHome = () => {
    const [metrics, setMetrics] = useState({
        totalEstudiantes: 0,
        mora: 0,
        pendientes: 0,
        aprobados: 0,
        rechazados: 0
    });
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                setLoading(true);
                const hoy = new Date().toISOString().split('T')[0];

                // 1. Consultar Estudiantes (Traemos solo lo necesario)
                const qEstudiantes = query(collection(db, "ninos"));
                const snapEstudiantes = await getDocs(qEstudiantes);
                const docsEstudiantes = snapEstudiantes.docs.map(doc => doc.data());

                // Cálculo de mora en cliente (ya que Firestore no permite comparaciones de fechas complejas fácilmente con otros filtros)
                const totalMora = docsEstudiantes.filter(est => 
                    est.proximaFechaPago < hoy && !est.pagoPendienteVerificacion
                ).length;

                // 2. Consultar Pagos por estados (Consultas independientes optimizadas)
                // Nota: Para ahorrar aún más, podrías usar getCountFromServer aquí si no necesitas los objetos
                const qPagos = query(collection(db, "pagos_recibidos"));
                const snapPagos = await getDocs(qPagos);
                const docsPagos = snapPagos.docs.map(doc => doc.data());

                setMetrics({
                    totalEstudiantes: snapEstudiantes.size,
                    mora: totalMora,
                    pendientes: docsPagos.filter(p => p.estado === "Pendiente").length,
                    aprobados: docsPagos.filter(p => p.estado === "Aprobado").length,
                    rechazados: docsPagos.filter(p => p.estado === "Rechazado").length,
                });

            } catch (error) {
                console.error("Error al obtener métricas:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, []);

    const datosGrafica = [
        { name: 'Pendientes', cantidad: metrics.pendientes, fill: '#fbbf24' },
        { name: 'Aprobados', cantidad: metrics.aprobados, fill: '#34d399' },
        { name: 'Rechazados', cantidad: metrics.rechazados, fill: '#f87171' }
    ];

    if (loading) return <div className="p-6 text-center text-gray-500">Cargando métricas...</div>;

    return (
        <div className="p-4 sm:p-6 bg-gray-50/50 min-h-screen">
            <h1 className="text-xl sm:text-2xl font-black mb-4 sm:mb-6 text-gray-800 tracking-tight">Dashboard General</h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
                
                {/* Total Estudiantes */}
                <div 
                    onClick={() => navigate('/admin/estudiantes')} 
                    className="bg-white p-5 sm:p-6 rounded-2xl sm:rounded-[2rem] shadow-sm border border-gray-100 border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-all group"
                >
                    <h2 className="text-gray-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-2">Total Estudiantes</h2>
                    <p className="text-3xl sm:text-4xl font-black text-gray-800 mb-1 group-hover:text-blue-600 transition-colors">{metrics.totalEstudiantes}</p>
                    <span className="text-[10px] sm:text-xs font-bold text-blue-600">Gestionar alumnos →</span>
                </div>

                {/* Estudiantes en Mora */}
                <div className="bg-white p-5 sm:p-6 rounded-2xl sm:rounded-[2rem] shadow-sm border border-gray-100 border-l-4 border-l-red-500">
                    <h2 className="text-gray-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-2">En Mora</h2>
                    <p className="text-3xl sm:text-4xl font-black text-gray-800 mb-1">{metrics.mora}</p>
                    <span className="text-[10px] sm:text-xs text-red-500 font-bold bg-red-50 px-2 py-1 rounded-md">Sin pago reportado</span>
                </div>

                {/* Pagos por Verificar */}
                <div 
                    onClick={() => navigate('/admin/pagos')} 
                    className="bg-white p-5 sm:p-6 rounded-2xl sm:rounded-[2rem] shadow-sm border border-gray-100 border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-all group"
                >
                    <h2 className="text-gray-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-2">Por Verificar</h2>
                    <p className="text-3xl sm:text-4xl font-black text-gray-800 mb-1 group-hover:text-amber-500 transition-colors">{metrics.pendientes}</p>
                    <span className="text-[10px] sm:text-xs text-amber-600 font-bold">Comprobantes nuevos →</span>
                </div>

                {/* Pagos Aprobados */}
                <div className="bg-white p-5 sm:p-6 rounded-2xl sm:rounded-[2rem] shadow-sm border border-gray-100 border-l-4 border-l-emerald-500">
                    <h2 className="text-gray-400 text-xs sm:text-sm font-bold uppercase tracking-wider mb-2">Aprobados</h2>
                    <p className="text-3xl sm:text-4xl font-black text-gray-800 mb-1">{metrics.aprobados}</p>
                    <span className="text-[10px] sm:text-xs text-emerald-600 font-bold bg-emerald-50 px-2 py-1 rounded-md">Este mes</span>
                </div>

            </div>

            <div className="bg-white p-4 sm:p-6 rounded-2xl sm:rounded-[2.5rem] shadow-sm border border-gray-100">
                <h2 className="text-base sm:text-lg font-black mb-6 text-gray-800 ml-2">Estado de Recaudos</h2>
                <div className="h-[250px] sm:h-[350px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={datosGrafica} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                            <XAxis 
                                dataKey="name" 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 'bold' }} 
                                dy={10}
                            />
                            <YAxis 
                                axisLine={false} 
                                tickLine={false} 
                                tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 'bold' }} 
                            />
                            <Tooltip 
                                cursor={{ fill: '#f8fafc' }}
                                contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                            />
                            <Legend wrapperStyle={{ paddingTop: '20px', fontSize: '12px', fontWeight: 'bold' }} />
                            <Bar 
                                dataKey="cantidad" 
                                name="Cantidad" 
                                radius={[8, 8, 0, 0]} 
                                barSize={40}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default DashboardHome;
// const DashboardHome = () => {

//     const [estudiantes, setEstudiantes] = useState([]);

//     useEffect(() => {
//         const q = query(collection(db, "niños"), orderBy("fechaRegistro", "desc"));
//         const unsubscribe = onSnapshot(q, (snapshot) => {
//             const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
//             setEstudiantes(docs);
//         });
//         return () => unsubscribe();
//     }, []);

//     const handleTest = () => {
//         const miNumero = '+573133185893';
//         // const santiNumero = '+573143516714';
//         enviarPruebaWhatsApp(miNumero);
//         // enviarPruebaWhatsApp(santiNumero);
//     };

//     return (
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//             <div className="bg-white p-8 rounded-[2.5rem] shadow-sm border border-gray-100">
//                 <div className="flex items-center gap-4 mb-6">
//                     <div className="p-3 bg-green-100 text-green-600 rounded-2xl">
//                         <MessageSquare size={24} />
//                     </div>
//                     <div>
//                         <h3 className="text-xl font-black text-gray-800">Prueba de Notificaciones</h3>
//                         <p className="text-gray-500 text-sm font-medium">Verifica cómo llegan los mensajes a los padres.</p>
//                     </div>
//                 </div>

//                 <button
//                     onClick={handleTest}
//                     className="group flex items-center justify-center gap-3 w-full bg-green-500 hover:bg-green-600 text-white font-black p-4 rounded-2xl shadow-lg shadow-green-100 transition-all active:scale-95"
//                 >
//                     <Send size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
//                     Enviar WhatsApp de Prueba
//                 </button>
//             </div>
//         </div>
//     );
// };